import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApi } from '../../services/api';
import { ClaimsFilterBar } from '../../components/claims/ClaimsFilterBar';
import { ClaimsTable } from '../../components/claims/ClaimsTable';
import { EnrichedClaim, ClaimsFilters } from '../../components/claims/types';

export default function EmployeeClaimsPage() {
    const { id } = useParams<{ id: string }>();
    const api = useApi();
    const [claims, setClaims] = useState<EnrichedClaim[]>([]);
    const [employee, setEmployee] = useState<{ id: string; name: string; email: string } | null>(null);
    const [games, setGames] = useState<{ id: string; name: string }[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filters, setFilters] = useState<ClaimsFilters>({
        gameId: '', startDate: '', endDate: '', status: ''
    });

    useEffect(() => { loadGames(); }, []);
    useEffect(() => { loadClaims(); }, [id, filters]);

    async function loadGames() {
        try {
            const result = await api.getGames();
            setGames((result.games || []).map((g: any) => ({ id: g.id, name: g.name })));
        } catch (error) { console.error('Failed to load games:', error); }
    }

    async function loadClaims() {
        if (!id) return;
        try {
            setLoading(true);
            setError(null);
            const activeFilters = Object.fromEntries(
                Object.entries(filters).filter(([_, v]) => v !== '')
            );
            const result = await api.getEmployeeClaims(id, Object.keys(activeFilters).length > 0 ? activeFilters : undefined);
            setClaims(result.claims || []);
            if (result.employee) setEmployee(result.employee);
        } catch (err: any) {
            console.error('Failed to load employee claims:', err);
            setClaims([]);
            setEmployee(null);
            if (err?.status === 404) {
                setError('Employee not found.');
            } else if (err?.status === 403) {
                setError('Access denied. You do not have permission to view this employee\'s claims.');
            } else {
                setError('Failed to load claims. Please try again.');
            }
        } finally { setLoading(false); }
    }

    return (
        <div>
            <Link to="/admin/employees" className="text-primary hover:opacity-80 mb-4 inline-block">
                ← Back to Employees
            </Link>
            <h1 className="text-3xl font-bold text-text-primary">
                All Claims {employee ? `— ${employee.name}` : ''}
            </h1>
            {employee && <p className="text-text-secondary mb-6">{employee.email}</p>}
            <ClaimsFilterBar games={games} filters={filters} onFilterChange={setFilters} />
            {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6">
                    <p className="text-red-400">{error}</p>
                    <button onClick={loadClaims} className="text-primary hover:opacity-80 text-sm mt-2">
                        Try again
                    </button>
                </div>
            )}
            {!error && <ClaimsTable claims={claims} loading={loading} emptyMessage="This employee has no claims." />}
        </div>
    );
}
